import { useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router";
import { AnimatePresence, LayoutGroup, motion } from "motion/react";
import { useApi } from "../api.ts";
import { Button, Icon, LoadError, Loading, spot } from "../ui/index.tsx";
import { CompatBadges, DetectionBadge, GameArt, GameStatusBadge, NotForSale } from "./_shared.tsx";
import type { Game } from "../../shared/types.ts";

export function GameCard({ game }: { game: Game }) {
  return (
    <article onMouseMove={spot} className="pane hud spotlight group flex w-full flex-col overflow-hidden transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-accent/40">
      <div className="relative">
        <GameArt game={game} className="aspect-[460/215] [&_img]:opacity-80 [&_img]:transition-[opacity,transform] [&_img]:duration-700 [&_img]:ease-out group-hover:[&_img]:scale-[1.06] group-hover:[&_img]:opacity-100" />
        <div className="absolute inset-0 bg-gradient-to-t from-surface via-transparent to-transparent" />
        <span className="absolute left-3 top-3 rounded-md bg-bg/70 px-1.5 py-0.5 text-[11px] font-medium uppercase tracking-wide text-muted backdrop-blur">{game.category}</span>
      </div>
      <div className="flex flex-1 flex-col gap-3 p-5">
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-lg font-semibold tracking-tight"><Link to={`/games/${game.id}`} className="after:absolute after:inset-0">{game.name}</Link></h3>
          <div className="flex shrink-0 flex-col items-end gap-1">
            <GameStatusBadge status={game.status} />
            <DetectionBadge status={game.detection_status} />
          </div>
        </div>
        <CompatBadges game={game} />
        <p className="line-clamp-3 text-sm leading-relaxed text-muted">{game.description}</p>
        {game.features.length > 0 && (
          <ul className="flex flex-wrap gap-1.5 pt-1">
            {game.features.slice(0, 8).map((f) => <li key={f} className="rounded-md border border-line bg-raised px-2 py-0.5 text-xs text-muted">{f}</li>)}
            {game.features.length > 8 && <li className="px-1 py-0.5 text-xs text-faint">+{game.features.length - 8} more</li>}
          </ul>
        )}
        <div className="mt-auto space-y-3 border-t border-line pt-4">
          {game.purchasable && game.pricing_tiers.length > 0 ? (
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted">From <span className="font-semibold text-fg">{game.pricing_tiers[0]!.price}</span></span>
              <Link to={`/games/${game.id}`}><Button size="sm">View</Button></Link>
            </div>
          ) : (
            // Above the stretched title link so the form stays clickable.
            <div className="relative z-10"><NotForSale game={game} compact /></div>
          )}
        </div>
      </div>
    </article>
  );
}

export function PublicHeading({ title, body, aside, kicker }: { title: string; body: string; aside?: React.ReactNode; kicker?: string }) {
  return (
    <div className="relative">
      <div className="relative mx-auto flex max-w-6xl flex-wrap items-end justify-between gap-4 px-6 pb-10 pt-14">
        <div className="max-w-2xl animate-rise">
          <div className="kicker mb-4 flex items-center gap-2 text-accent">
            <span className="size-1.5 rounded-full bg-accent shadow-[0_0_10px_var(--color-accent)]" />{kicker ?? `// ${title.toLowerCase()}`}
          </div>
          <h1 className="text-5xl font-bold uppercase leading-none tracking-tight sm:text-6xl">{title}</h1>
          <p className="mt-4 text-lg leading-relaxed text-muted">{body}</p>
        </div>
        {aside}
      </div>
      <div className="mx-auto h-px max-w-6xl bg-gradient-to-r from-accent/50 via-line to-transparent" />
    </div>
  );
}

const CATEGORY_LABEL: Record<string, string> = { fps: "FPS", survival: "Survival", sandbox: "Sandbox", other: "Other" };

/** Filters shown as chips: availability first, then whichever categories exist. */
function useFilters(all: Game[]) {
  const cats = [...new Set(all.map((g) => g.category))].filter((c) => c !== "other").sort();
  return [
    { id: "all", label: "All", test: () => true },
    { id: "available", label: "Available", test: (g: Game) => g.purchasable },
    { id: "soon", label: "Coming soon", test: (g: Game) => g.status === "development" },
    ...cats.map((c) => ({ id: c, label: CATEGORY_LABEL[c] ?? c, test: (g: Game) => g.category === c })),
  ];
}

function FilterChips({ filters, value, onChange, counts }: { filters: { id: string; label: string }[]; value: string; onChange: (id: string) => void; counts: Record<string, number> }) {
  return (
    <LayoutGroup id="game-filters">
      <div role="tablist" aria-label="Filter games" className="mb-8 flex flex-wrap gap-1.5">
        {filters.map((f) => {
          const on = f.id === value;
          return (
            <button key={f.id} role="tab" aria-selected={on} type="button" onClick={() => onChange(f.id)}
              className={`relative rounded-full border px-3.5 py-1.5 text-sm transition-colors ${on ? "border-transparent text-fg" : "border-line text-muted hover:border-line-strong hover:text-fg"}`}>
              {on && <motion.span layoutId="filter-pill" className="absolute inset-0 rounded-full border border-accent/50 bg-accent-soft shadow-[0_0_20px_-6px_var(--color-accent)]" transition={{ type: "spring", stiffness: 420, damping: 32 }} />}
              <span className="relative">{f.label} <span className="ml-0.5 tabular-nums text-faint">{counts[f.id] ?? 0}</span></span>
            </button>
          );
        })}
      </div>
    </LayoutGroup>
  );
}

export default function Games() {
  const games = useApi<Game[]>("/games");
  const [params, setParams] = useSearchParams();
  // Discontinued titles last; Array.sort is stable, so the server's order holds within each group.
  const all = [...(games.data ?? [])].sort((a, b) => Number(a.status === "discontinued") - Number(b.status === "discontinued"));
  const filters = useFilters(all);
  const active = filters.find((f) => f.id === params.get("filter")) ?? filters[0]!;
  const list = all.filter(active.test);
  const counts = Object.fromEntries(filters.map((f) => [f.id, all.filter(f.test).length]));
  // Stagger the cards in on first paint only; filter changes should feel instant.
  const firstPaint = useRef(true);
  useEffect(() => { if (games.data) firstPaint.current = false; }, [games.data]);
  const choose = (id: string) => setParams(id === "all" ? {} : { filter: id }, { replace: true, preventScrollReset: true });
  return (
    <>
      <PublicHeading title="Games" body="Every title ExoWare supports, with live status and detection straight from our servers."
        aside={<Link to="/status" className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-fg"><Icon name="activity" className="size-4" />System status</Link>} />
      <section className="mx-auto max-w-6xl px-6 py-12">
        {games.loading && !games.data ? <Loading /> : games.error && !games.data ? <LoadError error={games.error} retry={games.reload} /> : all.length === 0 ? (
          <p className="rounded-[var(--radius-card)] border border-dashed border-line py-12 text-center text-sm text-muted">New titles are on the way.</p>
        ) : (
          <>
            <FilterChips filters={filters} value={active.id} onChange={choose} counts={counts} />
            {/* Cards reflow into place when the filter changes; leaving ones shrink out without holding their slot (popLayout). */}
            <motion.div layout className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              <AnimatePresence mode="popLayout">
                {list.map((g, i) => (
                  <motion.div key={g.id} layout initial={{ opacity: 0, scale: 0.94, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.94 }}
                    transition={{ type: "spring", stiffness: 300, damping: 30, delay: firstPaint.current ? i * 0.06 : 0 }} className="flex">
                    <GameCard game={g} />
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>
            {list.length === 0 && <p className="py-12 text-center text-sm text-muted">Nothing here yet.</p>}
          </>
        )}
      </section>
    </>
  );
}
